import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ObjectLibrary } from '@/components/editor/ObjectLibrary';
import {
  MousePointer2, Square, Circle, Star, Type, Move,
  Pen, Users, LayoutGrid
} from 'lucide-react';

const tools = [
  { id: 'select', icon: MousePointer2, label: 'Select (V)' },
  { id: 'move', icon: Move, label: 'Move (M)' },
  { id: 'rect', icon: Square, label: 'Rectangle (R)' },
  { id: 'circle', icon: Circle, label: 'Circle (C)' },
  { id: 'star', icon: Star, label: 'Star (S)' },
  { id: 'text', icon: Type, label: 'Text (T)' },
  { id: 'pen', icon: Pen, label: 'Pen (P)' },
];

export function ToolPanel() {
  const { state, dispatch } = useAnimation();
  const { t } = useI18n();

  const selectTool = (toolId) => {
    dispatch({ type: 'SET_TOOL', payload: toolId });
  };

  const addCharType = (type, name, fill, scale = 1.5) => {
    const targetLayer = state.selectedLayerId || state.project.layers[0]?.id;
    if (!targetLayer) return;
    dispatch({
      type: 'ADD_OBJECT',
      payload: {
        layerId: targetLayer,
        object: {
          id: `obj-${type}-${Date.now()}`,
          type, name,
          x: state.project.width / 2, y: state.project.height / 2,
          width: 80, height: 160, rotation: 0,
          scaleX: scale, scaleY: scale, opacity: 1,
          fill, stroke: '', strokeWidth: 0,
          keyframes: [{ frame: 0, x: state.project.width / 2, y: state.project.height / 2, rotation: 0, scaleX: scale, scaleY: scale, opacity: 1 }]
        }
      }
    });
  };

  const addCharacter = () => addCharType('character', 'Batelka', '#f8a4c8', 1.8);
  const addAgata = () => addCharType('agata', 'Agata', '#4ade80', 1.5);
  const addBlondeBallerina = () => addCharType('blondeBallerina', 'Blond Baletka', '#f9a8d4', 2);
  const addSun = () => addCharType('sun', 'Slunicko', '#fbbf24', 1.5);
  const addSinger = () => addCharType('singer', 'Zpevacka', '#8b5cf6', 2);

  const addShape = (type) => {
    const targetLayer = state.selectedLayerId || state.project.layers[0]?.id;
    if (!targetLayer) return;

    const cx = state.project.width / 2;
    const cy = state.project.height / 2;
    const defaults = {
      rect: { width: 100, height: 80, fill: '#e879a0' },
      circle: { width: 60, height: 60, fill: '#f472b6' },
      star: { width: 50, height: 50, fill: '#fbbf24', numPoints: 5, innerRadius: 15, outerRadius: 30 },
      text: { width: 200, height: 40, fill: '#e2e8f0', text: 'Text', fontSize: 28 },
      ellipse: { width: 80, height: 50, fill: '#a78bfa' },
    };

    const shapeDefaults = defaults[type] || defaults.rect;

    dispatch({
      type: 'ADD_OBJECT',
      payload: {
        layerId: targetLayer,
        object: {
          id: `obj-${type}-${Date.now()}`,
          type,
          name: `${type.charAt(0).toUpperCase() + type.slice(1)}`,
          x: cx,
          y: cy,
          rotation: 0,
          scaleX: 1,
          scaleY: 1,
          opacity: 1,
          stroke: '',
          strokeWidth: 0,
          ...shapeDefaults,
          keyframes: [
            { frame: 0, x: cx, y: cy, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 }
          ]
        }
      }
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col items-center py-3 gap-0.5" data-testid="tool-panel">
        {tools.map((tool) => (
          <Tooltip key={tool.id}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`tool-${tool.id}`}
                className={`h-8 w-8 p-0 rounded-lg transition-all duration-150 ${
                  state.activeTool === tool.id
                    ? 'bg-rose-500/15 text-rose-300 shadow-sm shadow-rose-500/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
                }`}
                onClick={() => {
                  selectTool(tool.id);
                  if (['rect', 'circle', 'star', 'text'].includes(tool.id)) {
                    addShape(tool.id);
                    selectTool('select');
                  }
                }}
              >
                <tool.icon className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-[10px]">
              {tool.label}
            </TooltipContent>
          </Tooltip>
        ))}

        <div className="w-5 h-px bg-white/[0.06] my-2" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-pink-300/60 hover:text-pink-300 hover:bg-pink-500/10" onClick={addCharacter} data-testid="tool-add-character">
              <Users className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[10px]">{t('addBatelka')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-rose-300/60 hover:text-rose-300 hover:bg-rose-500/10" onClick={addBlondeBallerina} data-testid="tool-add-blonde">
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="5" r="4" fill="currentColor" opacity="0.8"/>
                <line x1="4" y1="10" x2="12" y2="10" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="6" y1="10" x2="5" y2="14" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="10" y1="10" x2="11" y2="14" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[10px]">{t('addBlondeBallerina')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-amber-300/60 hover:text-amber-300 hover:bg-amber-500/10" onClick={addSun} data-testid="tool-add-sun">
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="4" fill="currentColor"/>
                <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[10px]">{t('addSun')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-violet-300/60 hover:text-violet-300 hover:bg-violet-500/10" onClick={addSinger} data-testid="tool-add-singer">
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="5" r="4" fill="currentColor" opacity="0.8"/>
                <rect x="4" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.6"/>
                <line x1="12" y1="3" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="2" r="1.5" fill="currentColor"/>
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[10px]">{t('addSinger')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-green-300/60 hover:text-green-300 hover:bg-green-500/10" onClick={addAgata} data-testid="tool-add-agata">
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="5" r="4" fill="currentColor" opacity="0.8"/>
                <ellipse cx="8" cy="11" rx="5" ry="3" fill="currentColor" opacity="0.6"/>
                <ellipse cx="8" cy="15" rx="3" ry="2" fill="currentColor" opacity="0.4"/>
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[10px]">{t('addAgata')}</TooltipContent>
        </Tooltip>

        <div className="w-5 h-px bg-white/[0.06] my-2" />

        <ObjectLibrary
          trigger={
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-cyan-300/60 hover:text-cyan-300 hover:bg-cyan-500/10" data-testid="tool-object-library">
              <LayoutGrid className="w-3.5 h-3.5" />
            </Button>
          }
        />
      </div>
    </TooltipProvider>
  );
}
